import { createContext, Fragment, useCallback, useContext, useState, type CSSProperties, type ReactNode } from 'react';
import { he } from './i18n-he';

/** UI language. Hebrew (right-to-left) is the default; the choice is remembered per browser. */
export type Lang = 'he' | 'en';
const STORAGE_KEY = 'zerogap.lang';

function stored(): Lang {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (v === 'he' || v === 'en') return v;
  } catch {
    /* storage blocked */
  }
  return 'he';
}

let current: Lang = stored();

function apply(l: Lang) {
  const root = document.documentElement;
  root.lang = l;
  root.dir = l === 'he' ? 'rtl' : 'ltr';
  document.title = l === 'he' ? 'ZeroGap – גיבוי' : 'ZeroGap Backup';
}
apply(current);

export const getLang = () => current;
export const isRtl = () => current === 'he';
/** BCP 47 locale for Intl formatting. */
export const locale = () => (current === 'he' ? 'he-IL' : 'en-US');

const missing = new Set<string>();

/**
 * Translate an English source string. `{name}` placeholders are filled from `vars`.
 * A `|context` suffix disambiguates identical English words with different Hebrew ("From|time"); English drops it.
 * Strings without a translation (e.g. free text from the server) pass through unchanged.
 */
export function tr(s: string, vars?: Record<string, string | number>): string {
  const bar = s.indexOf('|');
  let out = bar >= 0 ? s.slice(0, bar) : s;
  if (current === 'he') {
    const h = he[s];
    if (h !== undefined) out = h;
    else if (import.meta.env.DEV && !missing.has(s)) {
      missing.add(s);
      console.warn('[i18n] no Hebrew for:', s);
    }
  }
  return vars ? out.replace(/\{(\w+)\}/g, (m, k: string) => (k in vars ? String(vars[k]) : m)) : out;
}

/**
 * Marks an English UI string that is translated later with tr(), e.g. values in a lookup table. It returns
 * the string unchanged; scripts/check-i18n.mjs uses it to require a Hebrew entry.
 */
export const msg = (s: string) => s;

/** Arrows that point the right way for the reading direction. */
export const arrow = {
  back: () => (isRtl() ? '→' : '←'),
  forward: () => (isRtl() ? '←' : '→'),
  crumb: () => (isRtl() ? '‹' : '›'),
};

const Ctx = createContext<{ lang: Lang; setLang: (l: Lang) => void }>({ lang: current, setLang: () => {} });

export function I18nProvider({ children }: { children: ReactNode }) {
  const [lang, setState] = useState<Lang>(current);
  const setLang = useCallback((l: Lang) => {
    current = l;
    try {
      localStorage.setItem(STORAGE_KEY, l);
    } catch {
      /* storage blocked */
    }
    apply(l);
    setState(l);
  }, []);
  // Remount on change: tr() and the formatting helpers read the module-level language during render.
  return (
    <Ctx.Provider value={{ lang, setLang }}>
      <Fragment key={lang}>{children}</Fragment>
    </Ctx.Provider>
  );
}

export const useLang = () => useContext(Ctx);

export function LangSwitch({ className, style }: { className?: string; style?: CSSProperties }) {
  const { lang, setLang } = useLang();
  const next: Lang = lang === 'he' ? 'en' : 'he';
  return (
    <button type="button" className={className} style={style} lang={next} onClick={() => setLang(next)}>
      {next === 'en' ? 'English' : 'עברית'}
    </button>
  );
}
